import { useToast } from '@components/toast/Toast'
import { AccessControl } from '@components/layout'
import { useUpdateChat } from '@services/chat/mutations/useUpdateChat.ts'
import { ArrowLeft, Pencil } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

import { ChatEmptyDescription } from '../ChatEmptyDescription'
import { ChatInactiveFooter } from './ChatInactiveFooter'
import { ChatMessage } from './ChatMessage'
import { MessageArea } from './MessageArea'

type ChatDetailProps = {
  data: ChatDetail
  user_id?: string
  onBack?: () => void
}

export function ChatDetailComponent(props: ChatDetailProps) {
  const { data } = props
  const { t } = useTranslation('chat')
  const toast = useToast()

  const messagesRef = useRef<HTMLDivElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [title, setTitle] = useState(data.title ?? '')
  const { mutate: updateChat } = useUpdateChat()

  useEffect(() => {
    if (data?.messages) {
      setTimeout(() => {
        messagesRef.current?.scrollTo({
          top: messagesRef.current.scrollHeight,
          behavior: 'smooth'
        })
      }, 250)
    }
  }, [data])

  const saveTitle = () => {
    if (title !== data.title) {
      updateChat({
        id: data.id,
        body: { title },
        params: {
          onSuccess: () => {
            toast.success({ message: t('form.chat.title_updated') })
            setIsEditing(false)
          },
          onError: () => {
            toast.error({ message: t('form.chat.title_failed') })
            setTitle(data.title ?? '')
          }
        }
      })
    } else {
      setIsEditing(false)
    }
  }

  const closeConversation = () =>
    updateChat({
      id: data.id,
      body: { title: data.title, is_active: false }
    })

  return (
    <Card className="flex h-[680px] flex-col p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            type="button"
            variant="ghostDark"
            size="icon"
            onClick={props.onBack}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {isEditing ? (
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => e.key === 'Enter' && saveTitle()}
              className="h-9 max-w-[280px]"
            />
          ) : (
            <>
              <span className="max-w-[280px] truncate font-display text-base font-bold text-ink">
                {data.title}
              </span>
              <Button
                type="button"
                variant="ghostDark"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsEditing(true)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
        <AccessControl role="ADMIN">
          {data.is_active && (
            <Button type="button" size="sm" onClick={closeConversation}>
              {t('form.common.close_chat')}
            </Button>
          )}
        </AccessControl>
      </div>

      <div
        ref={messagesRef}
        className="flex flex-1 flex-col gap-2 overflow-y-auto"
      >
        {data?.messages?.length === 0 ? (
          <ChatEmptyDescription text={t('form.common.no_messages')} />
        ) : (
          data?.messages?.map((message) => (
            <ChatMessage message={message} key={message.id} />
          ))
        )}
        {!data?.is_active && <ChatInactiveFooter chat={data} />}
      </div>

      {data.id && (
        <div className="mt-4">
          <MessageArea id={data.id} />
        </div>
      )}
    </Card>
  )
}
